import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "react-query";

import { getServiceDetail, toggleServiceLike, getServiceLikeStatus } from "apis";
import { getServiceReviews, followUser, unfollowUser, getIsFollowing } from "apis";
import { createBooking } from "apis";
import { PageHeader, Button, Loading } from "components";
import { useAppModal } from "components/common/AppModalProvider";
import ReportModal from "components/common/ReportModal";
import TossCheckout from "components/Payment/TossCheckout";
import PortOneCheckout from "components/Payment/PortOneCheckout";
import { useUserStore } from "store";
import { ServiceCategory } from "types";
import { ScheduleDTO } from "types/DTOs/service/service.dto";
import LoginPromptModal from "components/common/LoginPromptModal";
import { getImageUrl } from "utils/image.util";

const PAYMENT_PROVIDER = process.env.REACT_APP_PAYMENT_PROVIDER || "portone";

const getCategoryColor = (category: ServiceCategory): string => {
  switch (category) {
    case ServiceCategory.CHAT:
      return "bg-pink-100 text-pink-700";
    case ServiceCategory.GAME:
      return "bg-purple-100 text-purple-700";
    case ServiceCategory.TAROT:
      return "bg-violet-100 text-violet-700";
    case ServiceCategory.FEEDBACK:
      return "bg-amber-100 text-amber-700";
    case ServiceCategory.STUDY:
      return "bg-blue-100 text-blue-700";
    case ServiceCategory.FREE:
      return "bg-emerald-100 text-emerald-700";
    default:
      return "bg-achromatic-100 text-achromatic-600";
  }
};

/* ── Lightbox Component ── */
const ImageLightbox = ({
  images,
  startIndex,
  onClose,
}: {
  images: string[];
  startIndex: number;
  onClose: () => void;
}) => {
  const [idx, setIdx] = useState(startIndex);
  const src = images[idx]?.startsWith('http') ? images[idx] : `${process.env.REACT_APP_API_URL}/${images[idx]}`;

  return (
    <div className="fixed inset-0 z-[9999] bg-black/95 flex flex-col" onClick={onClose}>
      {/* Top bar */}
      <div className="flex justify-between items-center px-4 py-3 flex-shrink-0" onClick={e => e.stopPropagation()}>
        <span className="text-white/70 text-sm font-medium">{idx + 1} / {images.length}</span>
        <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/10 text-white flex items-center justify-center text-lg hover:bg-white/20">✕</button>
      </div>
      {/* Image */}
      <div className="flex-1 flex items-center justify-center px-4 overflow-auto" onClick={e => e.stopPropagation()}>
        <img
          src={src}
          alt=""
          className="max-w-full max-h-full object-contain select-none"
          style={{ touchAction: 'pinch-zoom' }}
          draggable={false}
        />
      </div>
      {/* Navigation */}
      {images.length > 1 && (
        <div className="flex justify-center gap-4 py-4 flex-shrink-0" onClick={e => e.stopPropagation()}>
          <button
            onClick={() => setIdx(i => (i > 0 ? i - 1 : images.length - 1))}
            className="w-10 h-10 rounded-full bg-white/10 text-white flex items-center justify-center text-lg hover:bg-white/20"
          >←</button>
          <button
            onClick={() => setIdx(i => (i < images.length - 1 ? i + 1 : 0))}
            className="w-10 h-10 rounded-full bg-white/10 text-white flex items-center justify-center text-lg hover:bg-white/20"
          >→</button>
        </div>
      )}
    </div>
  );
};

const ServiceDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const backTo = (location.state as any)?.from || null;
  const { showAlert, showConfirm } = useAppModal();
  const { user: currentUser } = useUserStore();

  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const dateScrollRef = useRef<HTMLDivElement>(null);
  const [selectedSchedule, setSelectedSchedule] = useState<ScheduleDTO | null>(null);
  const [isBooking, setIsBooking] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [currentImgIdx, setCurrentImgIdx] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [hostRating, setHostRating] = useState<number | null>(null);
  const [reviewCount, setReviewCount] = useState(0);
  const [reviews, setReviews] = useState<any[]>([]);
  const [isFollowing, setIsFollowing] = useState(false);
  const [showReport, setShowReport] = useState(false);
  const [checkoutBookingId, setCheckoutBookingId] = useState<string | null>(null);

  const isLoggedIn = !!localStorage.getItem("user_access_token") && !!localStorage.getItem("terms_agree");

  const {
    data: serviceData,
    isLoading,
    isError,
  } = useQuery(["service", id], () => getServiceDetail(id!), {
    enabled: !!id,
  });

  const service = serviceData?.data?.data;

  // Fetch like status + host rating + follow status
  useEffect(() => {
    if (!service || !isLoggedIn) return;
    getServiceLikeStatus(service.id).then(res => {
      setLiked(res.data.data.liked);
      setLikeCount(res.data.data.likeCount);
    }).catch(() => {});
    if (service.host?.id) {
      getServiceReviews(service.id).then(res => {
        const data = res.data.data;
        setHostRating(data.averageRating ?? null);
        setReviewCount(data.reviews?.length ?? 0);
        setReviews(data.reviews ?? []);
      }).catch(() => {});
      getIsFollowing(service.host.id).then(res => {
        setIsFollowing(res.data.data);
      }).catch(() => {});
    }
  }, [service, isLoggedIn]);

  const handleToggleLike = async () => {
    if (!isLoggedIn) { setShowLoginModal(true); return; }
    if (!service) return;
    try {
      const res = await toggleServiceLike(service.id);
      setLiked(res.data.data.liked);
      setLikeCount(res.data.data.likeCount);
    } catch (e) { console.error(e); }
  };

  const handleToggleFollow = async () => {
    if (!isLoggedIn) { setShowLoginModal(true); return; }
    if (!service?.host?.id) return;
    try {
      if (isFollowing) {
        await unfollowUser(service.host.id);
        setIsFollowing(false);
      } else {
        await followUser(service.host.id);
        setIsFollowing(true);
      }
    } catch (e) { console.error(e); }
  };

  // Group schedules by date
  const dateGroups = useMemo(() => {
    if (!service?.schedules) return {};
    const groups: Record<string, ScheduleDTO[]> = {};
    service.schedules.forEach((schedule) => {
      if (!groups[schedule.date]) groups[schedule.date] = [];
      groups[schedule.date].push(schedule);
    });
    Object.keys(groups).forEach((date) => {
      groups[date].sort((a, b) => a.startTime.localeCompare(b.startTime));
    });
    return groups;
  }, [service?.schedules]);

  // 지난 날짜(모든 슬롯 종료)를 제외하고 정렬
  const sortedDates = Object.keys(dateGroups)
    .filter((date) => {
      // 해당 날짜에 아직 종료되지 않은 슬롯이 하나라도 있으면 표시
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const dateObj = new Date(date);
      dateObj.setHours(0, 0, 0, 0);
      return dateObj >= today;
    })
    .sort();

  // 지나간 스케줄인지 확인
  const isSchedulePast = useCallback((schedule: ScheduleDTO) => {
    const endDateTime = new Date(`${schedule.date}T${schedule.endTime}`);
    return endDateTime.getTime() < Date.now();
  }, []);

  // 해당 날짜에 이용 가능한 슬롯이 있는지 (미예약 + 미종료)
  const hasAvailableSlots = useCallback((date: string) => {
    return dateGroups[date]?.some(s => !s.isBooked && !isSchedulePast(s)) ?? false;
  }, [dateGroups, isSchedulePast]);

  const handleBooking = async () => {
    if (!selectedSchedule || !service) return;
    if (!isLoggedIn) {
      setShowLoginModal(true);
      return;
    }

    // 임박 예약(Final Sale) 감지: 서비스 시작까지 2시간 이내
    const serviceStart = new Date(`${selectedSchedule.date}T${selectedSchedule.startTime}`);
    const msUntilStart = serviceStart.getTime() - Date.now();
    const isImminent = msUntilStart <= 2 * 60 * 60 * 1000 && msUntilStart > 0;

    if (isImminent) {
      const confirmed = await showConfirm(
        "⚠️ 시작 시간이 2시간 이내인 임박 예약입니다.\n결제 후에는 10분 이내를 제외하고 취소/환불이 불가합니다.\n결제하시겠습니까?",
        { title: '🔥 임박 예약 안내', confirmText: '결제하기', variant: 'warning' }
      );
      if (!confirmed) return;
    }

    setIsBooking(true);
    try {
      const bookingRes = await createBooking(selectedSchedule.id, service.id);
      const bookingId = bookingRes.data.data.id;
      // 예약 생성 후 바로 결제창 오픈
      setCheckoutBookingId(bookingId);
    } catch (error: any) {
      console.error("Booking failed:", error);
      const msg = error?.response?.data?.message || "예약 중 오류가 발생했습니다. 다시 시도해주세요.";
      showAlert(msg, { variant: 'error' });
    } finally {
      setIsBooking(false);
    }
  };

  const imgSrc = useCallback((img: string) => {
    return img.startsWith('http') ? img : `${process.env.REACT_APP_API_URL}/${img}`;
  }, []);

  const handleBack = useCallback(() => {
    if (backTo) {
      navigate(backTo);
    } else {
      navigate(-1);
    }
  }, [backTo, navigate]);

  if (isLoading) return (
    <div className="animate-fadeUp mobile:pb-[56px]">
      <PageHeader title="서비스 상세" isArrow onBack={handleBack} />
      <div className="bg-white rounded-2xl p-6 mobile:rounded-none">
        <Loading />
      </div>
    </div>
  );

  if (isError || !service) return (
    <div className="animate-fadeUp mobile:pb-[56px]">
      <PageHeader title="서비스 상세" isArrow onBack={handleBack} />
      <div className="bg-white rounded-2xl p-10 text-center text-achromatic-400 mobile:rounded-none">서비스를 찾을 수 없습니다.</div>
    </div>
  );

  if (bookingSuccess) return (
    <div className="animate-fadeUp mobile:pb-[56px]">
      <PageHeader title="예약 완료" />
      <div className="bg-white rounded-2xl mobile:rounded-none">
        <div className="flex flex-col items-center justify-center py-20 px-6 gap-4">
          <span className="text-5xl">🎉</span>
          <h2 className="text-xl font-bold text-achromatic-900">예약이 완료되었습니다!</h2>
          <p className="text-sm text-achromatic-400 text-center">
            서비스 시작 10분 전에 입장 링크가 활성화됩니다.
            <br />마이페이지에서 예약 내역을 확인하세요.
          </p>
          <div className="flex gap-3 mt-4">
            <button
              className="px-6 py-3 bg-primary-500 text-white rounded-xl font-bold text-sm hover:bg-primary-600 transition-colors"
              onClick={() => navigate("/mypage/bookings")}
            >
              내 예약 확인
            </button>
            <button
              className="px-6 py-3 bg-achromatic-50 text-achromatic-600 rounded-xl font-semibold text-sm hover:bg-achromatic-100 transition-colors"
              onClick={() => navigate("/service")}
            >
              서비스 더보기
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const imageCount = service.images?.length ?? 0;

  return (
    <div className="animate-fadeUp mobile:pb-[56px]">
      <PageHeader title="서비스 상세" isArrow onBack={handleBack} />

      <div className="bg-white rounded-2xl overflow-hidden mobile:rounded-none">
        {/* ── Image Gallery ── */}
        {imageCount > 0 ? (
          <div className="relative bg-achromatic-900">
            {/* Main image */}
            <div
              className="w-full flex items-center justify-center cursor-pointer"
              style={{ aspectRatio: '1/1', maxHeight: '420px' }}
              onClick={() => setLightboxOpen(true)}
            >
              <img
                src={imgSrc(service.images[currentImgIdx])}
                alt={`${service.title} ${currentImgIdx + 1}`}
                className="w-full h-full object-contain"
              />
            </div>

            {/* Arrow buttons */}
            {imageCount > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); setCurrentImgIdx(i => i > 0 ? i - 1 : imageCount - 1); }}
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 text-white flex items-center justify-center text-sm hover:bg-black/60 transition-colors"
                >←</button>
                <button
                  onClick={(e) => { e.stopPropagation(); setCurrentImgIdx(i => i < imageCount - 1 ? i + 1 : 0); }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/40 text-white flex items-center justify-center text-sm hover:bg-black/60 transition-colors"
                >→</button>
              </>
            )}

            {/* Counter badge */}
            {imageCount > 1 && (
              <div className="absolute bottom-3 right-3 bg-black/50 text-white text-[11px] font-medium px-2.5 py-1 rounded-full">
                {currentImgIdx + 1} / {imageCount}
              </div>
            )}

            {/* Dot indicators */}
            {imageCount > 1 && imageCount <= 10 && (
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                {service.images.map((_: string, idx: number) => (
                  <button
                    key={idx}
                    onClick={(e) => { e.stopPropagation(); setCurrentImgIdx(idx); }}
                    className={`w-2 h-2 rounded-full transition-all ${idx === currentImgIdx ? 'bg-white scale-110' : 'bg-white/40'}`}
                  />
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="w-full bg-gradient-to-br from-primary-50 via-violet-50 to-achromatic-50 flex items-center justify-center" style={{ aspectRatio: '4/3' }}>
            <span className="text-5xl opacity-50">
              {service.category === ServiceCategory.CHAT ? '💬' : service.category === ServiceCategory.GAME ? '🎮' : service.category === ServiceCategory.TAROT ? '🔮' : service.category === ServiceCategory.FEEDBACK ? '💡' : service.category === ServiceCategory.STUDY ? '💻' : '✨'}
            </span>
          </div>
        )}

        {/* Lightbox */}
        {lightboxOpen && service.images && (
          <ImageLightbox
            images={service.images}
            startIndex={currentImgIdx}
            onClose={() => setLightboxOpen(false)}
          />
        )}

        {/* Service info header */}
        <div className="flex flex-col px-6 py-5 border-b border-achromatic-50">
          <div className="flex justify-between items-start mb-3">
            <span
              className={`text-xs font-semibold px-2 py-1 rounded-md ${getCategoryColor(
                service.category
              )}`}
            >
              {service.category}
            </span>
            <div className="flex items-center gap-2">
              <span className="text-xs text-achromatic-400">
                ⏱ {service.durationMinutes}분/회
              </span>
            </div>
          </div>

          <h1 className="text-xl font-bold text-achromatic-900 mb-2 flex items-center gap-2">
            {service.title}
            <button onClick={handleToggleLike}
              className={`ml-auto flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-semibold transition-all ${
                liked ? 'bg-red-50 text-red-500' : 'bg-achromatic-50 text-achromatic-400 hover:bg-red-50 hover:text-red-400'
              }`}>
              <span className={`transition-transform ${liked ? 'scale-110' : ''}`}>{liked ? '❤️' : '🤍'}</span>
              {likeCount > 0 && <span className="text-[12px]">{likeCount}</span>}
            </button>
          </h1>
          <p className="text-sm text-achromatic-500 leading-relaxed whitespace-pre-wrap">
            {service.description}
          </p>

          {/* Host info — clickable */}
          <div className="flex items-center gap-3 mt-5 pt-4 border-t border-achromatic-50">
            <div className="flex items-center gap-3 flex-1 cursor-pointer" onClick={() => service.host?.profileId && navigate(`/@${service.host.profileId}`)}>
              {service.host?.profileImage ? (
                <img
                  src={getImageUrl(service.host.profileImage)}
                  alt="host"
                  className="w-10 h-10 rounded-full object-cover"
                />
              ) : (
                <div className="w-10 h-10 bg-achromatic-100 rounded-full flex items-center justify-center text-sm font-bold text-achromatic-500">
                  {service.host?.name?.charAt(0) ?? "?"}
                </div>
              )}
              <div>
                <div className="font-semibold text-sm text-achromatic-900 flex items-center gap-1.5">
                  {service.host?.name ?? "호스트"}
                  {hostRating !== null && (
                    <span className="text-[12px] text-amber-500 font-bold flex items-center gap-0.5">
                      ⭐ {hostRating.toFixed(1)}
                      <span className="text-achromatic-400 font-normal">({reviewCount})</span>
                    </span>
                  )}
                </div>
                <div className="text-xs text-achromatic-400">
                  @{service.host?.profileId}
                </div>
              </div>
            </div>
            {/* Only show follow button if NOT own service */}
            {(() => {
              const isOwnService = currentUser?.id && service.host?.id === currentUser.id;
              if (isOwnService) return null;
              return (
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button onClick={() => setShowReport(true)}
                    className="w-8 h-8 flex items-center justify-center bg-achromatic-50 rounded-lg text-achromatic-400 hover:bg-red-50 hover:text-red-500 transition-all text-[12px]"
                    title="신고">
                    ⚠️
                  </button>
                  <button onClick={handleToggleFollow}
                    className={`px-3 py-1.5 rounded-lg text-[12px] font-bold transition-all ${
                      isFollowing
                        ? 'bg-achromatic-100 text-achromatic-500 hover:bg-achromatic-200'
                        : 'bg-primary-500 text-white hover:bg-primary-600'
                    }`}>
                    {isFollowing ? '팔로잉' : '팔로우'}
                  </button>
                </div>
              );
            })()}
          </div>

          {/* Price */}
          <div className="flex items-baseline gap-1 mt-4 pt-4 border-t border-achromatic-50">
            <span className="text-2xl font-bold text-primary-500">
              {service.price.toLocaleString()}
            </span>
            <span className="text-sm text-achromatic-400">원 / 회</span>
          </div>
        </div>

        {/* ── Policies & Service Info ── */}
        <div className="px-6 py-5 border-b border-achromatic-50 flex flex-col gap-4">
          {/* Connection Guide */}
          <div>
            <h3 className="text-[13px] font-bold text-achromatic-800 mb-2 flex items-center gap-1.5">🔗 서비스 이용 안내</h3>
            <div className="bg-primary-50 rounded-xl p-3.5 text-[12px] text-primary-700 leading-relaxed">
              <ul className="list-disc list-inside flex flex-col gap-1">
                <li>결제 완료 후 서비스 시작 <strong>10분 전</strong>에 접속 링크가 활성화됩니다.</li>
                <li>호스트가 설정한 화상/음성 도구(Zoom, Discord 등)로 진행됩니다.</li>
                <li>서비스 시작 시간에 맞춰 입장해주세요.</li>
                <li>접속 링크는 마이페이지 &gt; 예약한 시간에서 확인할 수 있습니다.</li>
              </ul>
            </div>
          </div>

          {/* Cancellation Policy */}
          <div>
            <h3 className="text-[13px] font-bold text-achromatic-800 mb-2 flex items-center gap-1.5">💳 결제 및 취소 정책</h3>
            <div className="bg-achromatic-50 rounded-xl p-3.5 text-[12px] text-achromatic-600 leading-relaxed">
              <ul className="list-disc list-inside flex flex-col gap-1">
                <li>서비스 시작 <strong>2시간 전</strong>까지 전액 환불 가능합니다.</li>
                <li>서비스 시작 2시간 이내 예약(임박 예약)은 <strong>취소/환불 불가</strong>합니다.</li>
                <li>단, 결제 직후 <strong>10분 이내</strong>에는 실수 방지를 위해 취소가 가능합니다.</li>
                <li>호스트 사유로 취소 시 전액 환불됩니다.</li>
                <li>서비스 완료 후에는 환불이 불가합니다.</li>
              </ul>
            </div>
          </div>

          {/* No-show Policy */}
          <div>
            <h3 className="text-[13px] font-bold text-achromatic-800 mb-2 flex items-center gap-1.5">⏰ 노쇼 정책</h3>
            <div className="bg-amber-50 rounded-xl p-3.5 text-[12px] text-amber-700 leading-relaxed">
              <ul className="list-disc list-inside flex flex-col gap-1">
                <li>예약 시간으로부터 <strong>10분</strong> 이내에 입장하지 않을 경우 노쇼 처리됩니다.</li>
                <li>게스트 노쇼 시 환불이 불가합니다.</li>
                <li>호스트 노쇼 시 전액 환불되며, 경고 누적 시 <strong>계정 영구 정지</strong> 조치됩니다.</li>
              </ul>
            </div>
          </div>

          {/* Precautions */}
          <div>
            <h3 className="text-[13px] font-bold text-achromatic-800 mb-2 flex items-center gap-1.5">⚠️ 유의사항</h3>
            <div className="bg-achromatic-50 rounded-xl p-3.5 text-[12px] text-achromatic-500 leading-relaxed">
              <ul className="list-disc list-inside flex flex-col gap-1">
                <li>서비스 진행 중 상대방에 대한 비방, 욕설, 성희롱 등은 엄격히 금지됩니다.</li>
                <li>상호 동의 없는 녹화/녹음은 금지됩니다.</li>
                <li>문제 발생 시 <strong>help@jellypunch.com</strong>으로 연락해주세요.</li>
                <li>본 서비스는 호스트가 직접 등록한 것이며, 품질은 호스트에 의해 결정됩니다.</li>
              </ul>
            </div>
          </div>
        </div>

        {/* ── Reviews Section ── */}
        <div className="px-6 py-5 border-b border-achromatic-50">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-[15px] font-bold text-achromatic-900 flex items-center gap-1.5">
              ⭐ 리뷰
              {reviewCount > 0 && (
                <span className="text-achromatic-400 font-normal text-[13px]">({reviewCount})</span>
              )}
            </h2>
            {hostRating !== null && (
              <div className="flex items-center gap-1">
                <div className="flex">
                  {[1, 2, 3, 4, 5].map(star => (
                    <span key={star} className={`text-[14px] ${star <= Math.round(hostRating) ? 'text-amber-400' : 'text-achromatic-200'}`}>★</span>
                  ))}
                </div>
                <span className="text-[13px] font-bold text-achromatic-800 ml-1">{hostRating.toFixed(1)}</span>
              </div>
            )}
          </div>

          {reviews.length === 0 ? (
            <div className="text-center py-8 text-achromatic-300 text-sm">
              아직 리뷰가 없습니다.
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {reviews.slice(0, 5).map((review: any, idx: number) => (
                <div key={review.id ?? idx} className="bg-achromatic-50/50 rounded-xl p-4">
                  <div className="flex items-center gap-2.5 mb-2">
                    {review.reviewerProfileImage ? (
                      <img
                        src={review.reviewerProfileImage.startsWith('http') ? review.reviewerProfileImage : `${process.env.REACT_APP_API_URL}/${review.reviewerProfileImage}`}
                        alt=""
                        className="w-7 h-7 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-7 h-7 bg-achromatic-200 rounded-full flex items-center justify-center text-[10px] font-bold text-achromatic-500">
                        {review.reviewerName?.charAt(0) ?? '?'}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[12px] font-semibold text-achromatic-800">{review.reviewerName ?? '익명'}</span>
                        <div className="flex">
                          {[1, 2, 3, 4, 5].map(star => (
                            <span key={star} className={`text-[10px] ${star <= review.rating ? 'text-amber-400' : 'text-achromatic-200'}`}>★</span>
                          ))}
                        </div>
                      </div>
                      <span className="text-[10px] text-achromatic-300">
                        {review.createdAt ? new Date(review.createdAt).toLocaleDateString('ko-KR') : ''}
                      </span>
                    </div>
                  </div>
                  <p className="text-[12px] text-achromatic-600 leading-relaxed">{review.comment}</p>
                </div>
              ))}
              {reviews.length > 5 && (
                <button className="text-[12px] text-primary-500 font-semibold text-center py-2 hover:underline">
                  리뷰 {reviews.length - 5}개 더보기
                </button>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-5 border-b border-achromatic-50">
          <h2 className="text-[15px] font-bold text-achromatic-900 mb-3">📅 날짜 선택</h2>
          {sortedDates.length === 0 ? (
            <div className="text-center py-8 text-achromatic-300 text-sm">
              등록된 시간대가 없습니다.
            </div>
          ) : (
            <div className="relative">
              <div
                ref={dateScrollRef}
                className="flex gap-2 overflow-x-auto scrollbar-hide pb-1 pr-6"
                style={{ scrollSnapType: 'x mandatory', cursor: 'grab' }}
                onMouseDown={(e) => {
                  const el = dateScrollRef.current;
                  if (!el) return;
                  el.dataset.dragging = "true";
                  el.dataset.startX = String(e.pageX - el.offsetLeft);
                  el.dataset.scrollLeft = String(el.scrollLeft);
                  el.style.cursor = "grabbing";
                  el.style.scrollSnapType = "none";
                }}
                onMouseMove={(e) => {
                  const el = dateScrollRef.current;
                  if (!el || el.dataset.dragging !== "true") return;
                  e.preventDefault();
                  const x = e.pageX - el.offsetLeft;
                  const walk = (x - Number(el.dataset.startX));
                  el.scrollLeft = Number(el.dataset.scrollLeft) - walk;
                }}
                onMouseUp={() => {
                  const el = dateScrollRef.current;
                  if (!el) return;
                  el.dataset.dragging = "false";
                  el.style.cursor = "grab";
                  el.style.scrollSnapType = "x mandatory";
                }}
                onMouseLeave={() => {
                  const el = dateScrollRef.current;
                  if (!el) return;
                  el.dataset.dragging = "false";
                  el.style.cursor = "grab";
                  el.style.scrollSnapType = "x mandatory";
                }}
              >
                {sortedDates.map((date) => {
                  const available = dateGroups[date].filter((s) => !s.isBooked && !isSchedulePast(s)).length;
                  const isPast = !hasAvailableSlots(date);
                  const dateObj = new Date(date);
                  const month = dateObj.getMonth() + 1;
                  const day = dateObj.getDate();
                  const dayNames = ["일", "월", "화", "수", "목", "금", "토"];
                  const dayName = dayNames[dateObj.getDay()];
                  const isWeekend = dateObj.getDay() === 0 || dateObj.getDay() === 6;

                  return (
                    <button
                      key={date}
                      disabled={isPast}
                      onClick={() => {
                        if (isPast) return;
                        setSelectedDate(date);
                        setSelectedSchedule(null);
                      }}
                      className={`flex flex-col items-center min-w-[64px] px-3 py-3 rounded-xl border transition-all duration-150 ${
                        isPast
                          ? "border-achromatic-100 bg-achromatic-50 opacity-40 cursor-not-allowed"
                          : selectedDate === date
                          ? "border-primary-500 bg-primary-500/5"
                          : "border-achromatic-100 hover:border-achromatic-200"
                      }`}
                      style={{ scrollSnapAlign: 'start' }}
                    >
                      <span className="text-[10px] text-achromatic-400">{month}월</span>
                      <span
                        className={`text-lg font-bold ${
                          selectedDate === date ? "text-primary-500" : "text-achromatic-900"
                        }`}
                      >
                        {day}
                      </span>
                      <span
                        className={`text-[10px] ${
                          isWeekend ? "text-red-400" : "text-achromatic-400"
                        }`}
                      >
                        {dayName}
                      </span>
                      <span
                        className={`text-[10px] font-medium mt-1 ${
                          available > 0 ? "text-primary-500" : "text-achromatic-300"
                        }`}
                      >
                        {available > 0 ? `${available}개` : "마감"}
                      </span>
                    </button>
                  );
                })}
              </div>
              {/* 우측 스크롤 힌트 그라데이션 */}
              <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent pointer-events-none" />
            </div>
          )}
        </div>

        {/* Time slot selection */}
        {selectedDate && (
          <div className="px-6 py-5 border-b border-achromatic-50">
            <h2 className="text-[15px] font-bold text-achromatic-900 mb-3">⏰ 시간대 선택</h2>
            <div className="grid grid-cols-3 gap-2">
              {dateGroups[selectedDate]?.map((schedule) => {
                const past = isSchedulePast(schedule);
                const unavailable = schedule.isBooked || past;
                return (
                <button
                  key={schedule.id}
                  disabled={unavailable}
                  onClick={() => !unavailable && setSelectedSchedule(schedule)}
                  className={`flex flex-col items-center py-3 rounded-xl border text-sm font-medium transition-all duration-150 ${
                    unavailable
                      ? "border-achromatic-100 bg-achromatic-50 text-achromatic-300 cursor-not-allowed"
                      : selectedSchedule?.id === schedule.id
                      ? "border-primary-500 bg-primary-500/5 text-primary-500"
                      : "border-achromatic-100 text-achromatic-600 hover:border-primary-300"
                  }`}
                >
                  <span className="font-semibold">
                    {schedule.startTime.slice(0, 5)}
                  </span>
                  <span className="text-[10px] text-achromatic-400">
                    ~ {schedule.endTime.slice(0, 5)}
                  </span>
                  {schedule.isBooked && (
                    <span className="text-[10px] mt-1 text-achromatic-300">마감</span>
                  )}
                  {past && !schedule.isBooked && (
                    <span className="text-[10px] mt-1 text-achromatic-300">종료</span>
                  )}
                </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Booking summary & action */}
        {selectedSchedule && (
          <div className="px-6 py-5 sticky bottom-0 bg-white border-t border-achromatic-100">
            <div className="flex justify-between items-center mb-3">
              <div className="text-sm text-achromatic-500">
                <span className="font-medium text-achromatic-900">
                  {selectedDate} {selectedSchedule.startTime.slice(0, 5)} ~{" "}
                  {selectedSchedule.endTime.slice(0, 5)}
                </span>
              </div>
              <div className="text-lg font-bold text-primary-500">
                {service.price.toLocaleString()}원
              </div>
            </div>
            <p className="text-[11px] text-achromatic-400 leading-relaxed mb-3 flex items-start gap-1">
                <span className="text-amber-500 flex-shrink-0">⚠️</span>
                <span>
                  호스트가 제공하는 서비스에 대한 의무와 책임은 호스트에게 있습니다.
                  <span className="text-achromatic-300"> 결제하기 버튼을 누르면 위 면책 조항 및 환불 규정에 동의한 것으로 간주합니다.</span>
                </span>
              </p>
            <div className="h-[49px]">
              <Button
                text={isBooking ? "결제 중..." : "결제하기"}
                disabled={isBooking}
                onClick={handleBooking}
              />
            </div>
          </div>
        )}
      </div>

      <LoginPromptModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        message="서비스를 예약하려면 로그인이 필요합니다"
      />

      <ReportModal
        isOpen={showReport}
        onClose={() => setShowReport(false)}
        targetType="service"
        targetId={service.id}
        targetName={service.title}
      />

      {checkoutBookingId && service && (
        PAYMENT_PROVIDER === "portone" ? (
          <PortOneCheckout
            bookingId={checkoutBookingId}
            amount={service.price}
            orderName={service.title}
            customerName={currentUser?.name ?? "게스트"}
            onClose={() => setCheckoutBookingId(null)}
          />
        ) : (
          <TossCheckout
            bookingId={checkoutBookingId}
            amount={service.price}
            orderName={service.title}
            customerName={currentUser?.name ?? "게스트"}
            onClose={() => setCheckoutBookingId(null)}
          />
        )
      )}
    </div>
  );
};

export default ServiceDetail;
